import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { envKeyToggleRequiresConfirm } from '@/lib/confirm-risk';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  getConfig,
  getHealth,
  listCodebases,
  updateCodebase,
  updateWorkflowAdmissionRegistry,
  updateWorkflowAuditPresets,
  deleteCodebase,
  updateAssistantConfig,
  getCodebaseEnvVars,
  setCodebaseEnvVar,
  deleteCodebaseEnvVar,
  getAuthSession,
  getDatabaseValidationIssues,
  listOrganizations,
  loginAuthUser,
  logoutAuthUser,
  bootstrapAuthAdmin,
  listAuthUsers,
  upsertAuthUser,
  listAuthSessions,
  revokeAuthSession,
  listOrganizationInvites,
  createOrganizationInvite,
  revokeOrganizationInvite,
  acceptOrganizationInvite,
  requestAuthPasswordReset,
  resetAuthPassword,
  switchActiveOrganization,
  type AuthUserProfile,
  type AuthInvite,
  type DatabaseValidationIssueResponse,
  type DatabaseValidationIssuesResponse,
  type DatabaseValidationSummaryResponse,
  getDatabaseValidationSummary,
  listProjectSources,
  createProjectSource,
  updateProjectSource,
  archiveProjectSource,
} from '@/lib/api';
import type {
  SafeConfigResponse,
  CodebaseResponse,
  ProjectSourceCatalogEntry,
  ProjectSourceMutationInput,
  WorkflowAdmissionPolicyTemplate,
  WorkflowAuditSavedPreset,
} from '@/lib/api';
import {
  buildWorkflowAdmissionPolicyFromDraft,
  createWorkflowAdmissionPolicyDraft,
  formatWorkflowAdmissionPolicyJson,
  normalizeWorkflowAdmissionPolicyRecord,
  parseWorkflowAdmissionPolicyJson,
  summarizeWorkflowAdmissionPolicy,
  type WorkflowAdmissionPolicyDraft,
} from '@/lib/workflow-admission-policy';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { AddProjectDialog } from '@/components/projects/AddProjectDialog';
import { SecureKeysPanel } from '@/components/projects/SecureKeysPanel';
import { SecureStorageSection } from '@/components/projects/SecureStorageSection';
import { ProviderDashboard } from '@/components/providers/ProviderDashboard';
import { MosAuditFederationPanel } from '@/components/settings/MosAuditFederationPanel';
import { OperatorOrgPanel } from '@/components/settings/OperatorOrgPanel';
import { ManagedConfigPanel } from '@/components/settings/ManagedConfigPanel';
import { ProvisionPanel } from '@/components/settings/ProvisionPanel';
import { DeploymentPipelinePanel } from '@/components/settings/DeploymentPipelinePanel';
import { DeployMeshSetupPanel } from '@/components/settings/DeployMeshSetupPanel';
import { WebTerminal } from '@/components/terminal/WebTerminal';

const selectClass =
  'h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring [&>option]:bg-surface-elevated [&>option]:text-text-primary';

function SystemHealthSection({
  health,
  database,
}: {
  health:
    | {
        status: string;
        adapter: string;
        concurrency: { active: number; queuedTotal: number; maxConcurrent: number };
        runningWorkflows: number;
        version?: string;
      }
    | undefined;
  database: string | undefined;
}): React.ReactElement {
  const gitCommit = import.meta.env.VITE_GIT_COMMIT as string;
  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle>System Health</CardTitle>
      </CardHeader>
      <CardContent>
        {!health ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Status
              </div>
              <div className="mt-1">
                <Badge variant={health.status === 'ok' ? 'default' : 'destructive'}>
                  {health.status}
                </Badge>
              </div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Adapter
              </div>
              <div className="mt-1 font-medium">{health.adapter}</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Database
              </div>
              <div className="mt-1 font-medium">{database ?? 'unknown'}</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Active
              </div>
              <div className="mt-1 font-medium">
                {health.concurrency.active}/{health.concurrency.maxConcurrent}
              </div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Queued
              </div>
              <div className="mt-1 font-medium">{health.concurrency.queuedTotal}</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Workflows
              </div>
              <div className="mt-1 font-medium">{health.runningWorkflows}</div>
            </div>
            {health.version && (
              <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Version
                </div>
                <div className="mt-1 font-medium">{health.version}</div>
              </div>
            )}
            {gitCommit && gitCommit !== 'unknown' && (
              <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Commit
                </div>
                <div className="mt-1 font-mono font-medium">{gitCommit}</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AccountAuthSection(): React.ReactElement {
  const queryClient = useQueryClient();
  const { data: session } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
  });
  const { data: organizations } = useQuery({
    queryKey: ['authOrganizations'],
    queryFn: listOrganizations,
    enabled: session?.authenticated === true,
  });
  const { data: operators } = useQuery({
    queryKey: ['authUsers'],
    queryFn: listAuthUsers,
    enabled: session?.authenticated === true && session.operator?.role === 'admin',
  });
  const { data: sessions } = useQuery({
    queryKey: ['authSessions'],
    queryFn: listAuthSessions,
    enabled: session?.authenticated === true,
  });
  const { data: invites } = useQuery({
    queryKey: ['organizationInvites'],
    queryFn: listOrganizationInvites,
    enabled: session?.authenticated === true && session.operator?.role === 'admin',
  });

  const [bootstrapForm, setBootstrapForm] = useState({
    id: '',
    name: '',
    orgName: '',
    password: '',
  });
  const [loginForm, setLoginForm] = useState({
    id: '',
    password: '',
  });
  const [inviteAcceptForm, setInviteAcceptForm] = useState({
    token: '',
    name: '',
    password: '',
  });
  const [operatorForm, setOperatorForm] = useState<AuthUserProfile & { password: string }>({
    id: '',
    name: '',
    role: 'viewer',
    password: '',
  });
  const [inviteForm, setInviteForm] = useState({
    id: '',
    name: '',
    role: 'viewer' as AuthInvite['role'],
  });
  const [passwordResetRequestId, setPasswordResetRequestId] = useState('');
  const [issuedResetToken, setIssuedResetToken] = useState('');
  const [passwordResetForm, setPasswordResetForm] = useState({
    token: '',
    password: '',
  });

  const bootstrapMutation = useMutation({
    mutationFn: bootstrapAuthAdmin,
    onSuccess: async () => {
      setBootstrapForm({ id: '', name: '', orgName: '', password: '' });
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
    },
  });

  const loginMutation = useMutation({
    mutationFn: loginAuthUser,
    onSuccess: async () => {
      setLoginForm({ id: '', password: '' });
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
    },
  });

  const acceptInviteMutation = useMutation({
    mutationFn: acceptOrganizationInvite,
    onSuccess: async () => {
      setInviteAcceptForm({ token: '', name: '', password: '' });
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutAuthUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
      await queryClient.invalidateQueries({ queryKey: ['organizationInvites'] });
    },
  });

  const upsertMutation = useMutation({
    mutationFn: (profile: AuthUserProfile & { password: string }) =>
      upsertAuthUser(profile.id, {
        name: profile.name,
        role: profile.role,
        password: profile.password || undefined,
      }),
    onSuccess: async () => {
      setOperatorForm({ id: '', name: '', role: 'viewer', password: '' });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
    },
  });

  const switchOrgMutation = useMutation({
    mutationFn: switchActiveOrganization,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
      await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
      await queryClient.invalidateQueries({ queryKey: ['organizationInvites'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
    },
  });

  const revokeSessionMutation = useMutation({
    mutationFn: revokeAuthSession,
    onSuccess: async data => {
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
      if (data.revokedCurrentSession) {
        await queryClient.invalidateQueries({ queryKey: ['authOrganizations'] });
        await queryClient.invalidateQueries({ queryKey: ['authUsers'] });
        await queryClient.invalidateQueries({ queryKey: ['organizationInvites'] });
      }
    },
  });

  const createInviteMutation = useMutation({
    mutationFn: createOrganizationInvite,
    onSuccess: async data => {
      setInviteForm({ id: '', name: '', role: 'viewer' });
      if (data.invite?.token) {
        setInviteAcceptForm(current => ({
          ...current,
          token: data.invite?.token ?? current.token,
        }));
      }
      await queryClient.invalidateQueries({ queryKey: ['organizationInvites'] });
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: revokeOrganizationInvite,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organizationInvites'] });
    },
  });

  const requestResetMutation = useMutation({
    mutationFn: requestAuthPasswordReset,
    onSuccess: data => {
      setIssuedResetToken(data.resetToken);
      setPasswordResetForm(current => ({ ...current, token: data.resetToken }));
    },
  });

  const completeResetMutation = useMutation({
    mutationFn: resetAuthPassword,
    onSuccess: async () => {
      setPasswordResetForm({ token: '', password: '' });
      setIssuedResetToken('');
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
      await queryClient.invalidateQueries({ queryKey: ['authSessions'] });
    },
  });

  const errorMessage =
    bootstrapMutation.error instanceof Error
      ? bootstrapMutation.error.message
      : loginMutation.error instanceof Error
        ? loginMutation.error.message
        : acceptInviteMutation.error instanceof Error
          ? acceptInviteMutation.error.message
          : logoutMutation.error instanceof Error
            ? logoutMutation.error.message
            : upsertMutation.error instanceof Error
              ? upsertMutation.error.message
              : switchOrgMutation.error instanceof Error
                ? switchOrgMutation.error.message
                : revokeSessionMutation.error instanceof Error
                  ? revokeSessionMutation.error.message
                  : createInviteMutation.error instanceof Error
                    ? createInviteMutation.error.message
                    : revokeInviteMutation.error instanceof Error
                      ? revokeInviteMutation.error.message
                      : requestResetMutation.error instanceof Error
                        ? requestResetMutation.error.message
                        : completeResetMutation.error instanceof Error
                          ? completeResetMutation.error.message
                          : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account & Organization Auth</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Policy and admin mutations now bind to authenticated product accounts and organization
          membership instead of browser-local role headers.
        </p>
        {errorMessage && <div className="text-sm text-destructive">{errorMessage}</div>}
        {session?.authenticated && session.operator ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                Signed in as {session.operator.name} / {session.operator.role}
              </Badge>
              {session.operator.orgName && (
                <Badge variant="secondary">Org {session.operator.orgName}</Badge>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  logoutMutation.mutate();
                }}
              >
                Sign out
              </Button>
            </div>
            {organizations?.organizations.length ? (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-sm font-medium">Organization context</div>
                <div className="flex flex-wrap gap-2">
                  {organizations.organizations.map(org => {
                    const isActive = organizations.activeOrgId === org.id;
                    return (
                      <Button
                        key={org.id}
                        type="button"
                        variant={isActive ? 'default' : 'outline'}
                        onClick={() => {
                          if (!isActive) switchOrgMutation.mutate(org.id);
                        }}
                      >
                        {org.name}
                        {org.role ? ` / ${org.role}` : ''}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="space-y-2 border-t border-border pt-3">
              <div className="text-sm font-medium">Active sessions</div>
              <div className="space-y-2">
                {(sessions?.sessions ?? []).map(activeSession => (
                  <div
                    key={activeSession.id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium">{activeSession.activeOrgName}</div>
                      <div className="text-muted-foreground">
                        {activeSession.id}
                        {sessions?.currentSessionId === activeSession.id ? ' / current' : ''}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        revokeSessionMutation.mutate(activeSession.id);
                      }}
                    >
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {session.operator.role === 'admin' && (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="text-sm font-medium">Manage organization users</div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <Input
                    value={operatorForm.id}
                    onChange={event => {
                      setOperatorForm(current => ({ ...current, id: event.target.value }));
                    }}
                    placeholder="operator id"
                  />
                  <Input
                    value={operatorForm.name}
                    onChange={event => {
                      setOperatorForm(current => ({ ...current, name: event.target.value }));
                    }}
                    placeholder="display name"
                  />
                  <select
                    className={selectClass}
                    value={operatorForm.role}
                    onChange={event => {
                      setOperatorForm(current => ({
                        ...current,
                        role:
                          event.target.value === 'admin' || event.target.value === 'editor'
                            ? event.target.value
                            : 'viewer',
                      }));
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Input
                    type="password"
                    value={operatorForm.password}
                    onChange={event => {
                      setOperatorForm(current => ({ ...current, password: event.target.value }));
                    }}
                    placeholder="password (optional on update)"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    upsertMutation.mutate(operatorForm);
                  }}
                >
                  Save operator
                </Button>
                <div className="space-y-2">
                  {(operators?.operators ?? []).map(operator => (
                    <div
                      key={operator.id}
                      className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{operator.name}</div>
                        <div className="text-muted-foreground">
                          {operator.id} / {operator.role}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setOperatorForm({ ...operator, password: '' });
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {session.operator.role === 'admin' && (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="text-sm font-medium">Invites</div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Input
                    value={inviteForm.id}
                    onChange={event => {
                      setInviteForm(current => ({ ...current, id: event.target.value }));
                    }}
                    placeholder="invite id"
                  />
                  <Input
                    value={inviteForm.name}
                    onChange={event => {
                      setInviteForm(current => ({ ...current, name: event.target.value }));
                    }}
                    placeholder="display name"
                  />
                  <select
                    className={selectClass}
                    value={inviteForm.role}
                    onChange={event => {
                      setInviteForm(current => ({
                        ...current,
                        role:
                          event.target.value === 'admin' || event.target.value === 'editor'
                            ? event.target.value
                            : 'viewer',
                      }));
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    createInviteMutation.mutate(inviteForm);
                  }}
                >
                  Create invite
                </Button>
                {issuedResetToken ? (
                  <div className="rounded-md border border-border px-3 py-2 text-sm">
                    Latest password reset token:{' '}
                    <span className="font-mono">{issuedResetToken}</span>
                  </div>
                ) : null}
                <div className="space-y-2">
                  {(invites?.invites ?? []).map(invite => (
                    <div
                      key={invite.id}
                      className="rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">
                            {invite.invitedName} / {invite.role}
                          </div>
                          <div className="text-muted-foreground">
                            {invite.invitedOperatorId}
                            {invite.token ? ` / token ${invite.token}` : ''}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            revokeInviteMutation.mutate(invite.id);
                          }}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : session?.bootstrapRequired ? (
          <div className="space-y-3">
            <div className="text-sm font-medium">Bootstrap first governance admin</div>
            <div className="grid gap-3 sm:grid-cols-4">
              <Input
                value={bootstrapForm.id}
                onChange={event => {
                  setBootstrapForm(current => ({ ...current, id: event.target.value }));
                }}
                placeholder="operator id"
              />
              <Input
                value={bootstrapForm.name}
                onChange={event => {
                  setBootstrapForm(current => ({ ...current, name: event.target.value }));
                }}
                placeholder="display name"
              />
              <Input
                value={bootstrapForm.orgName}
                onChange={event => {
                  setBootstrapForm(current => ({ ...current, orgName: event.target.value }));
                }}
                placeholder="organization name"
              />
              <Input
                type="password"
                value={bootstrapForm.password}
                onChange={event => {
                  setBootstrapForm(current => ({ ...current, password: event.target.value }));
                }}
                placeholder="password"
              />
            </div>
            <Button
              type="button"
              onClick={() => {
                bootstrapMutation.mutate(bootstrapForm);
              }}
            >
              Bootstrap admin
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-medium">Sign in to manage organization policy</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                value={loginForm.id}
                onChange={event => {
                  setLoginForm(current => ({ ...current, id: event.target.value }));
                }}
                placeholder="operator id"
              />
              <Input
                type="password"
                value={loginForm.password}
                onChange={event => {
                  setLoginForm(current => ({ ...current, password: event.target.value }));
                }}
                placeholder="password"
              />
            </div>
            <Button
              type="button"
              onClick={() => {
                loginMutation.mutate(loginForm);
              }}
            >
              Sign in
            </Button>
            <div className="space-y-3 border-t border-border pt-3">
              <div className="text-sm font-medium">Accept organization invite</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Input
                  value={inviteAcceptForm.token}
                  onChange={event => {
                    setInviteAcceptForm(current => ({ ...current, token: event.target.value }));
                  }}
                  placeholder="invite token"
                />
                <Input
                  value={inviteAcceptForm.name}
                  onChange={event => {
                    setInviteAcceptForm(current => ({ ...current, name: event.target.value }));
                  }}
                  placeholder="display name override (optional)"
                />
                <Input
                  type="password"
                  value={inviteAcceptForm.password}
                  onChange={event => {
                    setInviteAcceptForm(current => ({ ...current, password: event.target.value }));
                  }}
                  placeholder="password"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  acceptInviteMutation.mutate(inviteAcceptForm);
                }}
              >
                Accept invite
              </Button>
            </div>
            <div className="space-y-3 border-t border-border pt-3">
              <div className="text-sm font-medium">Password reset</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  value={passwordResetRequestId}
                  onChange={event => {
                    setPasswordResetRequestId(event.target.value);
                  }}
                  placeholder="operator id"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    requestResetMutation.mutate(passwordResetRequestId);
                  }}
                >
                  Issue reset token
                </Button>
              </div>
              {issuedResetToken ? (
                <div className="rounded-md border border-border px-3 py-2 text-sm">
                  Issued reset token: <span className="font-mono">{issuedResetToken}</span>
                </div>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  value={passwordResetForm.token}
                  onChange={event => {
                    setPasswordResetForm(current => ({ ...current, token: event.target.value }));
                  }}
                  placeholder="reset token"
                />
                <Input
                  type="password"
                  value={passwordResetForm.password}
                  onChange={event => {
                    setPasswordResetForm(current => ({ ...current, password: event.target.value }));
                  }}
                  placeholder="new password"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  completeResetMutation.mutate(passwordResetForm);
                }}
              >
                Reset password
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EnvVarsPanel({ codebaseId }: { codebaseId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const { data: envVars } = useQuery({
    queryKey: ['codebaseEnvVars', codebaseId],
    queryFn: () => getCodebaseEnvVars(codebaseId),
  });

  const [mutationError, setMutationError] = useState<string | null>(null);

  const setMutation = useMutation({
    mutationFn: (data: { key: string; value: string }) => setCodebaseEnvVar(codebaseId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['codebaseEnvVars', codebaseId] });
      if (editingKey) {
        setEditingKey(null);
        setEditValue('');
      } else {
        setNewKey('');
        setNewValue('');
      }
      setMutationError(null);
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteCodebaseEnvVar(codebaseId, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['codebaseEnvVars', codebaseId] });
      setMutationError(null);
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  function handleAdd(e: React.FormEvent): void {
    e.preventDefault();
    if (newKey.trim() && newValue !== '') {
      setMutation.mutate({ key: newKey.trim(), value: newValue });
    }
  }

  function handleEditSave(key: string): void {
    if (editValue !== '') {
      setMutation.mutate({ key, value: editValue });
    }
  }

  const keys = envVars ?? [];

  return (
    <div className="mt-2 pl-2 border-l border-border space-y-2">
      {mutationError && <div className="text-xs text-destructive">{mutationError}</div>}
      {keys.length === 0 ? (
        <div className="text-xs text-muted-foreground">No env vars set.</div>
      ) : (
        <div className="space-y-1">
          {keys.map(key => (
            <div key={key} className="space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-text-primary truncate flex-1">{key}</span>
                <span className="text-muted-foreground">= ------</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs"
                  onClick={() => {
                    if (editingKey === key) {
                      setEditingKey(null);
                      setEditValue('');
                    } else {
                      setEditingKey(key);
                      setEditValue('');
                    }
                  }}
                >
                  {editingKey === key ? 'Cancel' : 'Edit'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs"
                  onClick={() => {
                    deleteMutation.mutate(key);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Remove
                </Button>
              </div>
              {editingKey === key && (
                <div className="flex gap-1 pl-2">
                  <Input
                    value={editValue}
                    onChange={e => {
                      setEditValue(e.target.value);
                    }}
                    placeholder="new value"
                    className="flex-1 h-7 text-xs"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEditSave(key);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      handleEditSave(key);
                    }}
                    disabled={setMutation.isPending}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleAdd} className="flex gap-1">
        <Input
          value={newKey}
          onChange={e => {
            setNewKey(e.target.value);
          }}
          placeholder="KEY"
          className="flex-1 h-7 text-xs font-mono"
        />
        <Input
          value={newValue}
          onChange={e => {
            setNewValue(e.target.value);
          }}
          placeholder="value"
          className="flex-1 h-7 text-xs"
        />
        <Button type="submit" size="sm" className="h-7 text-xs" disabled={setMutation.isPending}>
          Add
        </Button>
      </form>
    </div>
  );
}

function PolicyField({
  id,
  label,
  value,
  onChange,
  placeholder,
  description,
  inputMode = 'text',
  type = 'text',
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  description?: string;
  inputMode?: React.ComponentProps<'input'>['inputMode'];
  type?: React.ComponentProps<'input'>['type'];
  disabled?: boolean;
}): React.ReactElement {
  return (
    <label className="space-y-1.5 text-xs text-muted-foreground" htmlFor={id}>
      <div className="font-medium text-text-primary">{label}</div>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={e => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        className="h-9 text-sm"
        disabled={disabled}
      />
      {description && <div>{description}</div>}
    </label>
  );
}

function isWorkflowAuditPreset(value: unknown): value is WorkflowAuditSavedPreset {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as WorkflowAuditSavedPreset;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.createdAt === 'string' &&
    typeof candidate.state === 'object' &&
    candidate.state !== null &&
    !Array.isArray(candidate.state)
  );
}

function WorkflowAdmissionPolicyPanel({
  codebase,
  registryTemplates,
}: {
  codebase: CodebaseResponse;
  registryTemplates: WorkflowAdmissionPolicyTemplate[];
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [mode, setMode] = useState<'basic' | 'advanced'>('basic');
  const [basicDraft, setBasicDraft] = useState<WorkflowAdmissionPolicyDraft>(() =>
    createWorkflowAdmissionPolicyDraft(codebase.workflow_admission_policy, registryTemplates)
  );
  const [advancedDraft, setAdvancedDraft] = useState(() =>
    formatWorkflowAdmissionPolicyJson(codebase.workflow_admission_policy)
  );
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const selectedTemplate =
    registryTemplates.find(template => template.id === basicDraft.templateId) ?? null;
  const lockedFields = selectedTemplate?.lockedFields ?? [];

  useEffect(() => {
    const nextAdvancedDraft = formatWorkflowAdmissionPolicyJson(codebase.workflow_admission_policy);
    setBasicDraft(
      createWorkflowAdmissionPolicyDraft(codebase.workflow_admission_policy, registryTemplates)
    );
    setAdvancedDraft(nextAdvancedDraft);
  }, [codebase.id, codebase.workflow_admission_policy, registryTemplates]);

  const policyMutation = useMutation({
    mutationFn: (workflowAdmissionPolicy: CodebaseResponse['workflow_admission_policy']) =>
      updateCodebase(codebase.id, { workflowAdmissionPolicy }),
    onSuccess: updated => {
      const nextBasic = createWorkflowAdmissionPolicyDraft(
        updated.workflow_admission_policy,
        registryTemplates
      );
      const nextAdvanced = formatWorkflowAdmissionPolicyJson(updated.workflow_admission_policy);
      setBasicDraft(nextBasic);
      setAdvancedDraft(nextAdvanced);
      setMessage({ type: 'success', text: 'Policy saved.' });
      void queryClient.invalidateQueries({ queryKey: ['codebases'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  const previewPolicy = buildWorkflowAdmissionPolicyFromDraft(
    codebase.workflow_admission_policy,
    basicDraft,
    registryTemplates
  );
  const summary = summarizeWorkflowAdmissionPolicy(previewPolicy, registryTemplates);
  const lockedFieldSet = new Set(lockedFields);
  const isLocked = (field: string): boolean =>
    lockedFieldSet.has(field) || lockedFieldSet.has(`limits.${field}`);

  function handleSaveStructured(): void {
    const next = buildWorkflowAdmissionPolicyFromDraft(
      codebase.workflow_admission_policy,
      basicDraft,
      registryTemplates
    );
    policyMutation.mutate(next);
  }

  function handleSaveAdvanced(): void {
    const parsed = parseWorkflowAdmissionPolicyJson(advancedDraft);
    if (parsed.error) {
      setMessage({ type: 'error', text: parsed.error });
      return;
    }

    const nextPolicy =
      parsed.policy && basicDraft.templateId && !parsed.policy.templateId
        ? { ...parsed.policy, templateId: basicDraft.templateId }
        : parsed.policy;
    policyMutation.mutate(normalizeWorkflowAdmissionPolicyRecord(nextPolicy, registryTemplates));
  }

  function handleReset(): void {
    setBasicDraft(
      createWorkflowAdmissionPolicyDraft(codebase.workflow_admission_policy, registryTemplates)
    );
    setAdvancedDraft(formatWorkflowAdmissionPolicyJson(codebase.workflow_admission_policy));
    setMessage(null);
  }

  function handleClearPolicy(): void {
    void confirm({
      title: `Clear workflow admission policy for "${codebase.name}"?`,
      description: 'Removes the project override and returns control to repo or global defaults.',
      risk: 'moderate',
      confirmLabel: 'Clear policy',
    }).then(ok => {
      if (ok) policyMutation.mutate(null);
    });
  }

  function handleApplyTemplate(): void {
    if (!basicDraft.templateId) return;
    const template = registryTemplates.find(item => item.id === basicDraft.templateId);
    if (!template) return;
    const nextPolicy = { ...template.policy, templateId: template.id };
    setBasicDraft(createWorkflowAdmissionPolicyDraft(nextPolicy, registryTemplates));
    setAdvancedDraft(formatWorkflowAdmissionPolicyJson(nextPolicy));
    setMessage({
      type: 'success',
      text: `Loaded template "${template.name}". Save to apply it to this project.`,
    });
  }

  const hasSummary = summary.hasPolicy;

  return (
    <>
      <div className="mt-2 rounded-lg border border-border/70 bg-surface-elevated/40 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-sm font-semibold text-text-primary">Workflow admission policy</div>
            <div className="max-w-2xl text-xs leading-5 text-muted-foreground">
              Structured controls cover the common active-run limits. Advanced JSON stays available
              for custom fields, nested rules, or future policy extensions.
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            {hasSummary ? (
              <>
                {summary.limits.map(item => (
                  <Badge key={item.label} variant="outline" className="text-[10px]">
                    {item.label}: {item.value}
                  </Badge>
                ))}
                {summary.metadata.map(item => (
                  <Badge key={item.label} variant="secondary" className="text-[10px]">
                    {item.label}: {item.value}
                  </Badge>
                ))}
                {summary.lockedFields.map(item => (
                  <Badge
                    key={`${item.label}-${item.value}`}
                    variant="outline"
                    className="text-[10px]"
                  >
                    {item.label}: {item.value}
                  </Badge>
                ))}
                {summary.customFieldCount > 0 && (
                  <Badge variant="ghost" className="text-[10px]">
                    {summary.customFieldCount} custom field
                    {summary.customFieldCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </>
            ) : (
              <Badge variant="secondary" className="text-[10px]">
                No explicit policy
              </Badge>
            )}
          </div>
        </div>

        {registryTemplates.length > 0 && (
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-border/60 bg-background/70 px-3 py-2">
            <label className="min-w-[14rem] flex-1 space-y-1 text-xs text-muted-foreground">
              <div className="font-medium text-text-primary">Template</div>
              <select
                value={basicDraft.templateId}
                onChange={e => {
                  const nextTemplateId = e.target.value;
                  if (!nextTemplateId) {
                    setBasicDraft(current => ({ ...current, templateId: '' }));
                    setMessage(null);
                    return;
                  }

                  const template = registryTemplates.find(item => item.id === nextTemplateId);
                  if (!template) return;

                  const nextPolicy = { ...template.policy, templateId: template.id };
                  setBasicDraft(createWorkflowAdmissionPolicyDraft(nextPolicy, registryTemplates));
                  setAdvancedDraft(formatWorkflowAdmissionPolicyJson(nextPolicy));
                  setMessage({
                    type: 'success',
                    text: `Loaded template "${template.name}". Save to apply it to this project.`,
                  });
                }}
                className={selectClass}
              >
                <option value="">Select a reusable policy template</option>
                {registryTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={handleApplyTemplate}
              disabled={!basicDraft.templateId}
            >
              Load template
            </Button>
          </div>
        )}

        <Tabs
          value={mode}
          onValueChange={value => {
            setMode(value as 'basic' | 'advanced');
          }}
          className="mt-3"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Structured fields</TabsTrigger>
            <TabsTrigger value="advanced">Advanced JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <PolicyField
                id={`policy-${codebase.id}-source`}
                label="Policy source"
                value={basicDraft.source}
                onChange={value => {
                  setBasicDraft(current => ({ ...current, source: value }));
                }}
                placeholder="admin-ui"
                description="Optional note for operators and audit trails."
                disabled={isLocked('source')}
              />
              <PolicyField
                id={`policy-${codebase.id}-tenant`}
                label="Tenant ID"
                value={basicDraft.tenantId}
                onChange={value => {
                  setBasicDraft(current => ({ ...current, tenantId: value }));
                }}
                placeholder="tenant-42"
                description="Optional tenant scope for policy metadata."
                disabled={isLocked('tenantId')}
              />
              <PolicyField
                id={`policy-${codebase.id}-plan`}
                label="Plan"
                value={basicDraft.plan}
                onChange={value => {
                  setBasicDraft(current => ({ ...current, plan: value }));
                }}
                placeholder="pro"
                description="Optional plan label used by higher-level policy layers."
                disabled={isLocked('plan')}
              />
              <PolicyField
                id={`policy-${codebase.id}-global`}
                label="Max active global runs"
                value={basicDraft.maxActiveGlobal}
                onChange={value => {
                  setBasicDraft(current => ({ ...current, maxActiveGlobal: value }));
                }}
                placeholder="3"
                inputMode="numeric"
                type="number"
                description="Leave blank to inherit repo or global defaults."
                disabled={isLocked('maxActiveGlobal')}
              />
              <PolicyField
                id={`policy-${codebase.id}-per-codebase`}
                label="Max active per project"
                value={basicDraft.maxActivePerCodebase}
                onChange={value => {
                  setBasicDraft(current => ({ ...current, maxActivePerCodebase: value }));
                }}
                placeholder="1"
                inputMode="numeric"
                type="number"
                description="Caps concurrent runs for this codebase only."
                disabled={isLocked('maxActivePerCodebase')}
              />
            </div>

            <div className="rounded-md border border-border/60 bg-background/70 px-3 py-2 text-xs leading-5 text-muted-foreground">
              Structured edits preserve any custom JSON fields already saved on the project policy.
              {selectedTemplate && lockedFields.length > 0 && (
                <span className="ml-1">
                  Template locks are enforced for{' '}
                  {lockedFields.map(field => `\`${field}\``).join(', ')}.
                </span>
              )}
            </div>

            {message && (
              <div
                className={`text-xs ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
              >
                {message.text}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="text-xs"
                disabled={policyMutation.isPending}
                onClick={handleSaveStructured}
              >
                {policyMutation.isPending ? 'Saving...' : 'Save structured policy'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={handleReset}
                disabled={policyMutation.isPending}
              >
                Reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={handleClearPolicy}
                disabled={policyMutation.isPending}
              >
                Clear policy
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="mt-3 space-y-3">
            <div className="text-xs leading-5 text-muted-foreground">
              Edit the full policy object directly when you need nested limits or custom fields.
              Advanced JSON keeps template locks in place when saved.
            </div>
            <Textarea
              value={advancedDraft}
              onChange={e => {
                setAdvancedDraft(e.target.value);
              }}
              className="min-h-44 font-mono text-xs"
              spellCheck={false}
            />

            {message && (
              <div
                className={`text-xs ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
              >
                {message.text}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="text-xs"
                disabled={policyMutation.isPending}
                onClick={handleSaveAdvanced}
              >
                {policyMutation.isPending ? 'Saving...' : 'Save JSON'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={handleReset}
                disabled={policyMutation.isPending}
              >
                Reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={handleClearPolicy}
                disabled={policyMutation.isPending}
              >
                Clear policy
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      {confirmDialog}
    </>
  );
}

function WorkflowAuditPresetsPanel({
  codebase,
}: {
  codebase: CodebaseResponse;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() =>
    JSON.stringify(codebase.workflow_audit_presets ?? [], null, 2)
  );
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setDraft(JSON.stringify(codebase.workflow_audit_presets ?? [], null, 2));
  }, [codebase.workflow_audit_presets]);

  const mutation = useMutation({
    mutationFn: (presets: WorkflowAuditSavedPreset[]) =>
      updateCodebase(codebase.id, { workflowAuditPresets: presets }),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Project presets saved.' });
      void queryClient.invalidateQueries({ queryKey: ['codebases'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  function handleSave(): void {
    try {
      const parsed = JSON.parse(draft) as unknown;
      if (!Array.isArray(parsed)) {
        throw new Error('Saved presets must be a JSON array.');
      }
      const presets = parsed.filter(isWorkflowAuditPreset).slice(0, 12);
      if (presets.length !== parsed.length) {
        throw new Error('Each preset must include id, name, createdAt, and state.');
      }
      mutation.mutate(presets);
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Invalid JSON' });
    }
  }

  return (
    <div className="mt-2 space-y-3 rounded-md border border-border/70 bg-muted/10 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          Project-scoped dashboard presets travel with this codebase instead of the machine config.
        </div>
        <Badge variant="outline">
          {String(codebase.workflow_audit_presets?.length ?? 0)} presets
        </Badge>
      </div>
      <Textarea
        value={draft}
        onChange={e => {
          setDraft(e.target.value);
        }}
        className="min-h-48 font-mono text-xs"
        spellCheck={false}
      />
      {message && (
        <div
          className={`text-xs ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
        >
          {message.text}
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="text-xs" onClick={handleSave} disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : 'Save project presets'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => {
            setDraft(JSON.stringify(codebase.workflow_audit_presets ?? [], null, 2));
            setMessage(null);
          }}
          disabled={mutation.isPending}
        >
          Reset
        </Button>
      </div>
    </div>
  );
}

interface ProjectSourceFormState {
  id: string;
  label: string;
  description: string;
  kind: ProjectSourceCatalogEntry['kind'];
  provider: ProjectSourceCatalogEntry['provider'];
  repositoryUrl: string;
  baselineId: string;
  localMirrorPath: string;
  ref: string;
  visibility: NonNullable<ProjectSourceCatalogEntry['visibility']>;
  recommended: boolean;
  sortOrder: string;
  status: NonNullable<ProjectSourceCatalogEntry['status']>;
}

const emptyProjectSourceForm: ProjectSourceFormState = {
  id: '',
  label: '',
  description: '',
  kind: 'internal_reference',
  provider: 'gitea',
  repositoryUrl: '',
  baselineId: '',
  localMirrorPath: '',
  ref: 'main',
  visibility: 'internal',
  recommended: false,
  sortOrder: '0',
  status: 'active',
};

function projectSourceFormFromEntry(source: ProjectSourceCatalogEntry): ProjectSourceFormState {
  return {
    id: source.id,
    label: source.label,
    description: source.description,
    kind: source.kind,
    provider: source.provider,
    repositoryUrl: source.repositoryUrl ?? '',
    baselineId: source.baselineId ?? '',
    localMirrorPath: source.localMirrorPath ?? '',
    ref: source.ref ?? source.defaultRef ?? '',
    visibility: source.visibility ?? 'internal',
    recommended: source.recommended ?? false,
    sortOrder: String(source.sortOrder ?? 0),
    status: source.status ?? 'active',
  };
}

function projectSourcePayloadFromForm(form: ProjectSourceFormState): ProjectSourceMutationInput {
  return {
    id: form.id.trim() || undefined,
    label: form.label.trim(),
    description: form.description.trim(),
    kind: form.kind,
    provider: form.provider,
    repositoryUrl: form.repositoryUrl.trim() || null,
    baselineId: form.baselineId.trim() || null,
    localMirrorPath: form.localMirrorPath.trim() || null,
    ref: form.ref.trim() || null,
    visibility: form.visibility,
    recommended: form.recommended,
    sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
    status: form.status,
  };
}

function validateProjectSourceForm(form: ProjectSourceFormState): string | null {
  if (!form.label.trim()) return 'Label is required.';
  if (!form.kind) return 'Kind is required.';
  if (!form.provider) return 'Provider is required.';
  const hasRepositoryUrl = form.repositoryUrl.trim().length > 0;
  const hasLocalPath = form.localMirrorPath.trim().length > 0;
  if (form.kind === 'local_path' && !hasLocalPath) {
    return 'Local path sources require a local mirror path.';
  }
  if (
    (form.kind === 'git_url' || form.kind === 'internal_reference') &&
    !hasRepositoryUrl &&
    !hasLocalPath
  ) {
    return 'Git and internal sources require a repository URL or local mirror path.';
  }
  return null;
}

function ProjectSourceCatalogSection({
  operatorRole,
}: {
  operatorRole: string | undefined;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const isAdmin = operatorRole === 'admin';
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectSourceFormState>(emptyProjectSourceForm);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['projectSources'],
    queryFn: listProjectSources,
  });
  const sources = data?.sources ?? [];

  const createMutation = useMutation({
    mutationFn: (input: ProjectSourceMutationInput) => createProjectSource(input),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Project source created.' });
      setForm(emptyProjectSourceForm);
      void queryClient.invalidateQueries({ queryKey: ['projectSources'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProjectSourceMutationInput }) =>
      updateProjectSource(id, input),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Project source updated.' });
      setEditingId(null);
      setForm(emptyProjectSourceForm);
      void queryClient.invalidateQueries({ queryKey: ['projectSources'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveProjectSource(id),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Project source archived.' });
      void queryClient.invalidateQueries({ queryKey: ['projectSources'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  const isMutating =
    createMutation.isPending || updateMutation.isPending || archiveMutation.isPending;

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    if (!isAdmin) return;
    const validation = validateProjectSourceForm(form);
    if (validation) {
      setMessage({ type: 'error', text: validation });
      return;
    }
    const payload = projectSourcePayloadFromForm(form);
    setMessage(null);
    if (editingId) {
      updateMutation.mutate({ id: editingId, input: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Source Catalog</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error instanceof Error && (
          <div className="rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
            Failed to load project sources: {error.message}
          </div>
        )}
        {!isAdmin && (
          <div className="rounded-md border border-border bg-surface-muted/40 p-2 text-sm text-text-secondary">
            Source catalog editing requires an admin account.
          </div>
        )}
        {isAdmin && (
          <form onSubmit={handleSubmit} className="space-y-3 rounded-md border border-border p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                value={form.label}
                onChange={event => {
                  setForm(current => ({ ...current, label: event.target.value }));
                }}
                placeholder="Label"
                disabled={isMutating}
              />
              <Input
                value={form.id}
                onChange={event => {
                  setForm(current => ({ ...current, id: event.target.value }));
                }}
                placeholder="Source id"
                disabled={isMutating || Boolean(editingId)}
              />
              <select
                className={selectClass}
                value={form.kind}
                onChange={event => {
                  setForm(current => ({
                    ...current,
                    kind: event.target.value as ProjectSourceFormState['kind'],
                  }));
                }}
                disabled={isMutating}
              >
                <option value="internal_reference">Internal reference</option>
                <option value="git_url">Git URL</option>
                <option value="local_path">Local path</option>
              </select>
              <select
                className={selectClass}
                value={form.provider}
                onChange={event => {
                  setForm(current => ({
                    ...current,
                    provider: event.target.value as ProjectSourceFormState['provider'],
                  }));
                }}
                disabled={isMutating}
              >
                <option value="gitea">Gitea</option>
                <option value="github">GitHub</option>
                <option value="git">Git</option>
                <option value="filesystem">Filesystem</option>
              </select>
              <Input
                value={form.repositoryUrl}
                onChange={event => {
                  setForm(current => ({ ...current, repositoryUrl: event.target.value }));
                }}
                placeholder="Repository URL"
                disabled={isMutating}
              />
              <Input
                value={form.localMirrorPath}
                onChange={event => {
                  setForm(current => ({ ...current, localMirrorPath: event.target.value }));
                }}
                placeholder="Local mirror path"
                disabled={isMutating}
              />
              <Input
                value={form.baselineId}
                onChange={event => {
                  setForm(current => ({ ...current, baselineId: event.target.value }));
                }}
                placeholder="Baseline id"
                disabled={isMutating}
              />
              <Input
                value={form.ref}
                onChange={event => {
                  setForm(current => ({ ...current, ref: event.target.value }));
                }}
                placeholder="Ref"
                disabled={isMutating}
              />
              <Input
                value={form.sortOrder}
                onChange={event => {
                  setForm(current => ({ ...current, sortOrder: event.target.value }));
                }}
                placeholder="Sort order"
                disabled={isMutating}
              />
              <select
                className={selectClass}
                value={form.visibility}
                onChange={event => {
                  setForm(current => ({
                    ...current,
                    visibility: event.target.value as ProjectSourceFormState['visibility'],
                  }));
                }}
                disabled={isMutating}
              >
                <option value="internal">Internal</option>
                <option value="released">Released</option>
              </select>
            </div>
            <Textarea
              value={form.description}
              onChange={event => {
                setForm(current => ({ ...current, description: event.target.value }));
              }}
              placeholder="Description"
              disabled={isMutating}
            />
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.recommended}
                  onChange={event => {
                    setForm(current => ({ ...current, recommended: event.target.checked }));
                  }}
                  disabled={isMutating}
                />
                Recommended
              </label>
              <select
                className={selectClass}
                value={form.status}
                onChange={event => {
                  setForm(current => ({
                    ...current,
                    status: event.target.value as ProjectSourceFormState['status'],
                  }));
                }}
                disabled={isMutating}
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            {message && (
              <div
                className={`text-sm ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
              >
                {message.text}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm" disabled={isMutating}>
                {editingId ? 'Save source' : 'Add source'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isMutating}
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyProjectSourceForm);
                  setMessage(null);
                }}
              >
                Reset
              </Button>
            </div>
          </form>
        )}
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading project sources...</div>
        ) : sources.length === 0 ? (
          <div className="text-sm text-muted-foreground">No active project sources.</div>
        ) : (
          <div className="space-y-2">
            {sources.map(source => (
              <div key={source.id} className="rounded-md border border-border p-3 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-text-primary">{source.label}</span>
                      {source.recommended && <Badge variant="outline">recommended</Badge>}
                      {source.registered && <Badge variant="outline">already registered</Badge>}
                    </div>
                    <div className="mt-1 text-text-secondary">{source.description}</div>
                    <div className="mt-1 truncate font-mono text-[11px] text-text-tertiary">
                      {source.repositoryUrl ?? source.localMirrorPath ?? source.id}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-text-tertiary">
                      <span>{source.kind}</span>
                      <span>{source.provider}</span>
                      <span>sort {String(source.sortOrder ?? 0)}</span>
                      {source.ref && <span>ref {source.ref}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        onClick={() => {
                          setEditingId(source.id);
                          setForm(projectSourceFormFromEntry(source));
                          setMessage(null);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        onClick={() => {
                          archiveMutation.mutate(source.id);
                        }}
                      >
                        Archive
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectsSection({
  registryTemplates,
}: {
  registryTemplates: WorkflowAdmissionPolicyTemplate[];
}): React.ReactElement {
  const { confirm, confirmDialog } = useConfirm();
  const summarizeProjectSource = (
    codebase: CodebaseResponse
  ): { badge: string | null; detail: string | null } => {
    const source = codebase.project_source;
    if (!source) {
      return {
        badge: null,
        detail: codebase.repository_url,
      };
    }

    if (source.kind === 'internal_reference') {
      return {
        badge: 'internal baseline',
        detail: source.baselineId ?? source.repositoryUrl ?? codebase.repository_url,
      };
    }

    if (source.kind === 'git_url') {
      return {
        badge: 'git source',
        detail: source.repositoryUrl ?? codebase.repository_url,
      };
    }

    return {
      badge: 'local path',
      detail: source.repositoryUrl ?? codebase.repository_url,
    };
  };

  const queryClient = useQueryClient();
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<{
    id: string;
    section: 'env' | 'secure' | 'policy' | 'presets';
  } | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const { data: codebases } = useQuery({
    queryKey: ['codebases'],
    queryFn: listCodebases,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCodebase(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['codebases'] });
    },
  });

  const toggleEnvKeysMutation = useMutation({
    mutationFn: ({ id, allowEnvKeys }: { id: string; allowEnvKeys: boolean }) =>
      updateCodebase(id, { allowEnvKeys }),
    onSuccess: () => {
      setToggleError(null);
      void queryClient.invalidateQueries({ queryKey: ['codebases'] });
    },
    onError: (err: Error) => {
      // Without this the user clicks "Revoke env keys", confirms the
      // destructive dialog, and gets no feedback if the PATCH fails.
      setToggleError(err.message);
    },
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {toggleError && (
            <div className="mb-2 rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
              Failed to update env-key consent: {toggleError}
            </div>
          )}
          {!codebases || codebases.length === 0 ? (
            <div className="text-sm text-muted-foreground">No projects registered.</div>
          ) : (
            <div className="space-y-2">
              {codebases.map((cb: CodebaseResponse) => {
                const sourceSummary = summarizeProjectSource(cb);
                return (
                  <div key={cb.id} className="rounded-md border border-border p-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium truncate">{cb.name}</div>
                          {cb.allow_env_keys && (
                            <Badge variant="destructive" className="text-[10px]">
                              env keys allowed
                            </Badge>
                          )}
                          {sourceSummary.badge && (
                            <Badge variant="outline" className="text-[10px]">
                              {sourceSummary.badge}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {cb.default_cwd}
                        </div>
                        {sourceSummary.detail && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {sourceSummary.detail}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            const apply = (): void => {
                              toggleEnvKeysMutation.mutate({
                                id: cb.id,
                                allowEnvKeys: !cb.allow_env_keys,
                              });
                            };
                            // Granting consent is immediate; revoking is confirmed.
                            if (!envKeyToggleRequiresConfirm(cb.allow_env_keys)) {
                              apply();
                              return;
                            }
                            void confirm({
                              title: `Revoke env-key consent for "${cb.name}"?`,
                              description:
                                'Future workflow runs will be blocked if .env contains sensitive keys.',
                              risk: 'moderate',
                              confirmLabel: 'Revoke',
                            }).then(ok => {
                              if (ok) apply();
                            });
                          }}
                          disabled={toggleEnvKeysMutation.isPending}
                        >
                          {cb.allow_env_keys ? 'Revoke env keys' : 'Allow env keys'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setExpandedSection(
                              expandedSection?.id === cb.id && expandedSection.section === 'env'
                                ? null
                                : { id: cb.id, section: 'env' }
                            );
                          }}
                        >
                          Env Vars{' '}
                          {expandedSection?.id === cb.id && expandedSection.section === 'env'
                            ? '\u25B2'
                            : '\u25BC'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setExpandedSection(
                              expandedSection?.id === cb.id && expandedSection.section === 'secure'
                                ? null
                                : { id: cb.id, section: 'secure' }
                            );
                          }}
                        >
                          Secure Keys{' '}
                          {expandedSection?.id === cb.id && expandedSection.section === 'secure'
                            ? '\u25B2'
                            : '\u25BC'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setExpandedSection(
                              expandedSection?.id === cb.id && expandedSection.section === 'policy'
                                ? null
                                : { id: cb.id, section: 'policy' }
                            );
                          }}
                        >
                          Policy{' '}
                          {expandedSection?.id === cb.id && expandedSection.section === 'policy'
                            ? '\u25B2'
                            : '\u25BC'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setExpandedSection(
                              expandedSection?.id === cb.id && expandedSection.section === 'presets'
                                ? null
                                : { id: cb.id, section: 'presets' }
                            );
                          }}
                        >
                          Presets{' '}
                          {expandedSection?.id === cb.id && expandedSection.section === 'presets'
                            ? '\u25B2'
                            : '\u25BC'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            void confirm({
                              title: `Remove project "${cb.name}"?`,
                              description:
                                'Removes the project from Maestro and deletes its workspace directory and worktrees. This cannot be undone.',
                              risk: 'high',
                              confirmLabel: 'Remove',
                            }).then(ok => {
                              if (ok) deleteMutation.mutate(cb.id);
                            });
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                    {expandedSection?.id === cb.id && expandedSection.section === 'env' && (
                      <EnvVarsPanel codebaseId={cb.id} />
                    )}
                    {expandedSection?.id === cb.id && expandedSection.section === 'secure' && (
                      <SecureKeysPanel codebaseId={cb.id} />
                    )}
                    {expandedSection?.id === cb.id && expandedSection.section === 'policy' && (
                      <WorkflowAdmissionPolicyPanel
                        codebase={cb}
                        registryTemplates={registryTemplates}
                      />
                    )}
                    {expandedSection?.id === cb.id && expandedSection.section === 'presets' && (
                      <WorkflowAuditPresetsPanel codebase={cb} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              setAddProjectOpen(true);
            }}
          >
            + Add Project
          </Button>

          <AddProjectDialog
            open={addProjectOpen}
            onOpenChange={setAddProjectOpen}
            onSuccess={() => {
              void queryClient.invalidateQueries({ queryKey: ['codebases'] });
            }}
          />
        </CardContent>
      </Card>
      {confirmDialog}
    </>
  );
}

function DatabaseValidationSection({
  sessionAuthenticated,
  summary,
  issues,
  summaryError,
  issuesError,
}: {
  sessionAuthenticated: boolean;
  summary: DatabaseValidationSummaryResponse | undefined;
  issues: DatabaseValidationIssuesResponse | undefined;
  summaryError: Error | null;
  issuesError: Error | null;
}): React.ReactElement {
  const sampleRowClass =
    'flex items-start justify-between gap-3 rounded-md border border-border/60 px-3 py-2 text-xs';
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<
    'all' | 'governance' | 'deployment' | 'learning'
  >('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'error' | 'warn' | 'info'>('all');
  const [activeTab, setActiveTab] = useState<'governance' | 'deployment' | 'learning'>(
    'governance'
  );

  useEffect(() => {
    if (!issues?.issues.length) {
      setSelectedIssueId(null);
      return;
    }
    setSelectedIssueId(current =>
      current && issues.issues.some(issue => issue.id === current)
        ? current
        : (issues.issues[0]?.id ?? null)
    );
  }, [issues]);

  const filteredIssues =
    issues?.issues.filter(issue => {
      if (domainFilter !== 'all' && issue.domain !== domainFilter) return false;
      if (severityFilter !== 'all' && issue.severity !== severityFilter) return false;
      return true;
    }) ?? [];

  const selectedIssue =
    filteredIssues.find(issue => issue.id === selectedIssueId) ?? filteredIssues[0] ?? null;

  function focusValidationTab(tab: 'governance' | 'deployment' | 'learning'): void {
    setActiveTab(tab);
    const el = document.getElementById(`settings-validation-${tab}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  if (!sessionAuthenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Database Validation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Sign in with an authenticated account to validate governed database content.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Database Validation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {summaryError || issuesError ? (
          <div className="text-sm text-destructive">
            Failed to load database validation data.{' '}
            {summaryError?.message ?? issuesError?.message ?? 'Unknown error'}
          </div>
        ) : !summary || !issues ? (
          <div className="text-sm text-muted-foreground">Loading validation summary...</div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span>
                Read-only snapshot for validating governance, deployment, and learning data.
              </span>
              <Badge variant="outline">{new Date(summary.generatedAt).toLocaleString()}</Badge>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-md border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-text-tertiary">Governance</div>
                <div className="mt-2 space-y-1 text-sm">
                  <div>Artifacts: {summary.governance.artifacts.total}</div>
                  <div>Active artifacts: {summary.governance.artifacts.active}</div>
                  <div>Non-verified drift: {summary.governance.artifacts.drifted}</div>
                  <div>Validation rules: {summary.governance.validationRules.total}</div>
                </div>
              </div>

              <div className="rounded-md border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-text-tertiary">Deployment</div>
                <div className="mt-2 space-y-1 text-sm">
                  <div>Profiles: {summary.deployment.profiles.total}</div>
                  <div>Manifests: {summary.deployment.manifests.total}</div>
                  <div>Required manifests: {summary.deployment.manifests.required}</div>
                </div>
              </div>

              <div className="rounded-md border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-text-tertiary">Learning</div>
                <div className="mt-2 space-y-1 text-sm">
                  <div>Recent events sampled: {summary.learning.events.total}</div>
                  <div>Lessons tracked: {summary.learning.lessons.total}</div>
                  <div>Best practices tracked: {summary.learning.bestPractices.total}</div>
                  <div>Open drift findings: {summary.learning.driftFindings.open}</div>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">Validation Issues</div>
                  <div className="text-xs text-text-tertiary">
                    Drill into non-active, drifted, or unresolved records without browsing raw
                    tables.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Total {issues.totals.total}</Badge>
                  <Badge variant={issues.totals.error > 0 ? 'destructive' : 'outline'}>
                    Errors {issues.totals.error}
                  </Badge>
                  <Badge variant={issues.totals.warn > 0 ? 'secondary' : 'outline'}>
                    Warnings {issues.totals.warn}
                  </Badge>
                  <Badge variant="outline">Info {issues.totals.info}</Badge>
                </div>
              </div>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="font-medium text-text-primary">Domain filter</div>
                  <select
                    aria-label="Validation domain filter"
                    value={domainFilter}
                    onChange={e => {
                      const next =
                        e.target.value === 'governance' ||
                        e.target.value === 'deployment' ||
                        e.target.value === 'learning'
                          ? e.target.value
                          : 'all';
                      setDomainFilter(next);
                    }}
                    className={selectClass}
                  >
                    <option value="all">All domains</option>
                    <option value="governance">Governance</option>
                    <option value="deployment">Deployment</option>
                    <option value="learning">Learning</option>
                  </select>
                </label>
                <label className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="font-medium text-text-primary">Severity filter</div>
                  <select
                    aria-label="Validation severity filter"
                    value={severityFilter}
                    onChange={e => {
                      const next =
                        e.target.value === 'error' ||
                        e.target.value === 'warn' ||
                        e.target.value === 'info'
                          ? e.target.value
                          : 'all';
                      setSeverityFilter(next);
                    }}
                    className={selectClass}
                  >
                    <option value="all">All severities</option>
                    <option value="error">Error</option>
                    <option value="warn">Warn</option>
                    <option value="info">Info</option>
                  </select>
                </label>
              </div>

              {filteredIssues.length === 0 ? (
                <div className="mt-3 text-sm text-muted-foreground">
                  No validation issues match the current filters.
                </div>
              ) : (
                <div className="mt-3 grid gap-3 lg:grid-cols-[1.2fr_0.8fr]">
                  <div className="space-y-2">
                    {filteredIssues.map(issue => (
                      <button
                        key={issue.id}
                        type="button"
                        onClick={(): void => {
                          setSelectedIssueId(issue.id);
                          setActiveTab(issue.contextTab);
                        }}
                        className={`w-full rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                          issue.id === selectedIssueId
                            ? 'border-primary bg-primary/5'
                            : 'border-border/60 hover:bg-surface-elevated'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{issue.title}</div>
                            <div className="truncate text-text-tertiary">{issue.summary}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant={
                                issue.severity === 'error'
                                  ? 'destructive'
                                  : issue.severity === 'warn'
                                    ? 'secondary'
                                    : 'outline'
                              }
                            >
                              {issue.severity}
                            </Badge>
                            <Badge variant="outline">{issue.domain}</Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  <ValidationIssueDetail
                    issue={selectedIssue}
                    onOpenContext={tab => {
                      focusValidationTab(tab);
                    }}
                  />
                </div>
              )}
            </div>

            <Tabs
              value={activeTab}
              onValueChange={value => {
                setActiveTab(value as typeof activeTab);
              }}
              className="space-y-3"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="governance">Governance</TabsTrigger>
                <TabsTrigger value="deployment">Deployment</TabsTrigger>
                <TabsTrigger value="learning">Learning</TabsTrigger>
              </TabsList>

              <TabsContent value="governance" className="space-y-3">
                <div id="settings-validation-governance" />
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Sample Artifacts</div>
                    {summary.governance.sampleArtifacts.length === 0 ? (
                      <div className="text-xs text-text-tertiary">No artifacts found.</div>
                    ) : (
                      summary.governance.sampleArtifacts.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.title}</div>
                            <div className="truncate text-text-tertiary">
                              {item.canonicalPath ?? 'No path'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant="outline">{item.artifactType}</Badge>
                            <Badge
                              variant={
                                item.driftStatus === 'verified' ? 'secondary' : 'destructive'
                              }
                            >
                              {item.driftStatus}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Sample Validation Rules</div>
                    {summary.governance.sampleRules.length === 0 ? (
                      <div className="text-xs text-text-tertiary">No validation rules found.</div>
                    ) : (
                      summary.governance.sampleRules.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.title}</div>
                            <div className="truncate text-text-tertiary">
                              {item.appliesTo.length > 0
                                ? item.appliesTo.join(', ')
                                : 'No applies-to scope'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant={item.severity === 'critical' ? 'destructive' : 'outline'}
                            >
                              {item.severity}
                            </Badge>
                            <Badge variant="secondary">{item.status}</Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="deployment" className="space-y-3">
                <div id="settings-validation-deployment" />
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Sample Deployment Profiles</div>
                    {summary.deployment.sampleProfiles.length === 0 ? (
                      <div className="text-xs text-text-tertiary">
                        No deployment profiles found.
                      </div>
                    ) : (
                      summary.deployment.sampleProfiles.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.displayName}</div>
                            <div className="truncate text-text-tertiary">
                              {item.environmentType}
                            </div>
                          </div>
                          <Badge variant="secondary">{item.status}</Badge>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Sample Seed Manifests</div>
                    {summary.deployment.sampleManifests.length === 0 ? (
                      <div className="text-xs text-text-tertiary">
                        No deployment manifests found.
                      </div>
                    ) : (
                      summary.deployment.sampleManifests.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.manifestKey}</div>
                            <div className="truncate text-text-tertiary">
                              {item.targetArea} • v{item.version}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant={item.isRequired ? 'default' : 'outline'}>
                              {item.isRequired ? 'required' : 'optional'}
                            </Badge>
                            <Badge variant="secondary">{item.status}</Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="learning" className="space-y-3">
                <div id="settings-validation-learning" />
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Recent Learning Events</div>
                    {summary.learning.recentEvents.length === 0 ? (
                      <div className="text-xs text-text-tertiary">No learning events found.</div>
                    ) : (
                      summary.learning.recentEvents.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.summary}</div>
                            <div className="truncate text-text-tertiary">
                              {item.domain} • {item.eventType}
                            </div>
                          </div>
                          <Badge variant="secondary">{item.status}</Badge>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Recent Drift Findings</div>
                    {summary.learning.recentDriftFindings.length === 0 ? (
                      <div className="text-xs text-text-tertiary">No drift findings found.</div>
                    ) : (
                      summary.learning.recentDriftFindings.map(item => (
                        <div key={item.id} className={sampleRowClass}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{item.summary}</div>
                            <div className="truncate text-text-tertiary">{item.findingType}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant={item.severity === 'error' ? 'destructive' : 'outline'}>
                              {item.severity}
                            </Badge>
                            <Badge variant="secondary">{item.status}</Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ValidationIssueDetail({
  issue,
  onOpenContext,
}: {
  issue: DatabaseValidationIssueResponse | null;
  onOpenContext: (tab: DatabaseValidationIssueResponse['contextTab']) => void;
}): React.ReactElement {
  if (!issue) {
    return (
      <div className="rounded-md border border-dashed border-border/60 p-3 text-sm text-muted-foreground">
        Select an issue to inspect its record detail.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{issue.title}</div>
          <div className="text-xs text-text-tertiary">{issue.summary}</div>
        </div>
        <Badge
          variant={
            issue.severity === 'error'
              ? 'destructive'
              : issue.severity === 'warn'
                ? 'secondary'
                : 'outline'
          }
        >
          {issue.severity}
        </Badge>
      </div>

      <div className="mt-3 space-y-2 text-xs">
        <div>
          <span className="text-text-tertiary">Domain: </span>
          <span className="font-medium">{issue.domain}</span>
        </div>
        <div>
          <span className="text-text-tertiary">Record type: </span>
          <span className="font-medium">{issue.recordType}</span>
        </div>
        <div>
          <span className="text-text-tertiary">Record id: </span>
          <span className="font-mono">{issue.recordId}</span>
        </div>
        <div>
          <span className="text-text-tertiary">Record label: </span>
          <span className="font-medium">{issue.recordLabel}</span>
        </div>
        <div>
          <span className="text-text-tertiary">Recorded path: </span>
          <span>{issue.sourcePath ?? 'No source path recorded.'}</span>
        </div>
        <div>
          <span className="text-text-tertiary">Status: </span>
          <Badge variant="outline">{issue.status}</Badge>
        </div>
        <div>
          <span className="text-text-tertiary">Detail: </span>
          <span>{issue.detail ?? 'No additional detail recorded.'}</span>
        </div>
        <div className="pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => {
              onOpenContext(issue.contextTab);
            }}
          >
            Jump to {issue.contextTab} records
          </Button>
        </div>
      </div>
    </div>
  );
}

function AssistantConfigSection({ config }: { config: SafeConfigResponse }): React.ReactElement {
  const queryClient = useQueryClient();
  const [assistant, setAssistant] = useState(config.assistant);
  const [claudeModel, setClaudeModel] = useState(config.assistants.claude.model ?? 'sonnet');
  const [codexModel, setCodexModel] = useState(config.assistants.codex.model ?? '');
  const [reasoning, setReasoning] = useState<'minimal' | 'low' | 'medium' | 'high' | 'xhigh'>(
    config.assistants.codex.modelReasoningEffort ?? 'medium'
  );
  const [webSearch, setWebSearch] = useState<'disabled' | 'cached' | 'live'>(
    config.assistants.codex.webSearchMode ?? 'disabled'
  );
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const hasChanges =
    assistant !== config.assistant ||
    claudeModel !== (config.assistants.claude.model ?? 'sonnet') ||
    codexModel !== (config.assistants.codex.model ?? '') ||
    reasoning !== (config.assistants.codex.modelReasoningEffort ?? 'medium') ||
    webSearch !== (config.assistants.codex.webSearchMode ?? 'disabled');

  useEffect(() => {
    setAssistant(config.assistant);
    setClaudeModel(config.assistants.claude.model ?? 'sonnet');
    setCodexModel(config.assistants.codex.model ?? '');
    setReasoning(config.assistants.codex.modelReasoningEffort ?? 'medium');
    setWebSearch(config.assistants.codex.webSearchMode ?? 'disabled');
  }, [config]);

  const mutation = useMutation({
    mutationFn: updateAssistantConfig,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaveMsg({ type: 'success', text: 'Settings saved.' });
      setTimeout(() => {
        setSaveMsg(null);
      }, 3000);
    },
    onError: (err: Error) => {
      setSaveMsg({ type: 'error', text: err.message });
    },
  });

  function handleSave(): void {
    mutation.mutate({
      assistant,
      claude: { model: claudeModel },
      // The generated type requires `model` when `codex` is present; omit the codex key
      // entirely when no model is set so the server treats it as "no codex changes".
      ...(codexModel
        ? {
            codex: { model: codexModel, modelReasoningEffort: reasoning, webSearchMode: webSearch },
          }
        : {}),
    });
  }

  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle>Assistant Configuration</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="rounded-xl border border-border/60 bg-background/70 p-4">
            <div className="mb-3 text-sm text-muted-foreground">
              Choose the default operator model and tune Codex reasoning and web-search posture.
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2 text-sm">
              <label htmlFor="default-assistant">Default Assistant</label>
              <select
                id="default-assistant"
                value={assistant}
                onChange={e => {
                  setAssistant(e.target.value as 'claude' | 'codex');
                }}
                className={selectClass}
              >
                <option value="claude">Claude</option>
                <option value="codex">Codex</option>
              </select>

              <label htmlFor="claude-model">Claude Model</label>
              <select
                id="claude-model"
                value={claudeModel}
                onChange={e => {
                  setClaudeModel(e.target.value);
                }}
                className={selectClass}
              >
                <option value="sonnet">sonnet</option>
                <option value="opus">opus</option>
                <option value="haiku">haiku</option>
              </select>

              <label htmlFor="codex-model">Codex Model</label>
              <Input
                id="codex-model"
                value={codexModel}
                onChange={e => {
                  setCodexModel(e.target.value);
                }}
                placeholder="gpt-5.3-codex"
              />

              <label htmlFor="reasoning">Reasoning Effort</label>
              <select
                id="reasoning"
                value={reasoning}
                onChange={e => {
                  setReasoning(e.target.value as 'minimal' | 'low' | 'medium' | 'high' | 'xhigh');
                }}
                className={selectClass}
              >
                <option value="minimal">minimal</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
              </select>

              <label htmlFor="web-search">Web Search</label>
              <select
                id="web-search"
                value={webSearch}
                onChange={e => {
                  setWebSearch(e.target.value as 'disabled' | 'cached' | 'live');
                }}
                className={selectClass}
              >
                <option value="disabled">disabled</option>
                <option value="cached">cached</option>
                <option value="live">live</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={mutation.isPending || !hasChanges} size="sm">
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            {saveMsg && (
              <span
                className={`text-sm ${saveMsg.type === 'success' ? 'text-success' : 'text-destructive'}`}
              >
                {saveMsg.text}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PolicyRegistrySection({ config }: { config: SafeConfigResponse }): React.ReactElement {
  const queryClient = useQueryClient();
  const [templates, setTemplates] = useState<WorkflowAdmissionPolicyTemplate[]>(
    config.workflowAdmissionRegistry?.templates ?? []
  );
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setTemplates(config.workflowAdmissionRegistry?.templates ?? []);
  }, [config.workflowAdmissionRegistry]);

  const mutation = useMutation({
    mutationFn: (nextTemplates: WorkflowAdmissionPolicyTemplate[]) =>
      updateWorkflowAdmissionRegistry({ templates: nextTemplates }),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Policy registry saved.' });
      void queryClient.invalidateQueries({ queryKey: ['config'] });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  function updateTemplate(
    templateId: string,
    updater: (current: WorkflowAdmissionPolicyTemplate) => WorkflowAdmissionPolicyTemplate
  ): void {
    setTemplates(current => current.map(item => (item.id === templateId ? updater(item) : item)));
  }

  function addTemplate(): void {
    const id = globalThis.crypto?.randomUUID?.() ?? `template-${Date.now()}`;
    setTemplates(current => [
      ...current,
      {
        id,
        name: 'New policy template',
        description: '',
        tags: [],
        owner: 'platform-admin',
        rolloutState: 'draft',
        allowCodebaseOverride: true,
        lockedFields: [],
        policy: { source: 'registry' },
      },
    ]);
    setMessage(null);
  }

  const governanceOverview = useMemo(() => {
    const overview = {
      total: templates.length,
      draft: 0,
      active: 0,
      archived: 0,
      overrideLocked: 0,
      overrideAllowed: 0,
      owned: 0,
    };

    for (const template of templates) {
      switch (template.rolloutState ?? 'active') {
        case 'draft':
          overview.draft += 1;
          break;
        case 'archived':
          overview.archived += 1;
          break;
        default:
          overview.active += 1;
          break;
      }
      if (template.allowCodebaseOverride === false) overview.overrideLocked += 1;
      if (template.allowCodebaseOverride !== false) overview.overrideAllowed += 1;
      if ((template.owner ?? '').trim()) overview.owned += 1;
    }

    return overview;
  }, [templates]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Policy Registry</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Reusable workflow admission templates for common plans, tenants, and governance modes.
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">{String(governanceOverview.total)} templates</Badge>
          <Badge variant="secondary">{String(governanceOverview.active)} active</Badge>
          <Badge variant="outline">{String(governanceOverview.draft)} draft</Badge>
          <Badge variant="outline">{String(governanceOverview.archived)} archived</Badge>
          <Badge variant="outline">{String(governanceOverview.owned)} owned</Badge>
          <Badge variant={governanceOverview.overrideLocked > 0 ? 'destructive' : 'outline'}>
            {String(governanceOverview.overrideLocked)} override locked
          </Badge>
          <Badge variant="outline">
            {String(governanceOverview.overrideAllowed)} override allowed
          </Badge>
        </div>

        {templates.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
            No templates saved yet.
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(template => (
              <div key={template.id} className="rounded-md border border-border p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={template.rolloutState === 'archived' ? 'secondary' : 'outline'}>
                    {(template.owner ?? 'unowned').trim() || 'unowned'}
                  </Badge>
                  <Badge
                    variant={
                      template.rolloutState === 'draft'
                        ? 'secondary'
                        : template.rolloutState === 'archived'
                          ? 'outline'
                          : 'default'
                    }
                  >
                    {template.rolloutState ?? 'active'}
                  </Badge>
                  <Badge
                    variant={template.allowCodebaseOverride === false ? 'destructive' : 'outline'}
                  >
                    {template.allowCodebaseOverride === false
                      ? 'Overrides locked'
                      : 'Overrides allowed'}
                  </Badge>
                  {template.lockedFields?.length ? (
                    <Badge variant="outline">
                      {String(template.lockedFields.length)} locked fields
                    </Badge>
                  ) : null}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <PolicyField
                    id={`registry-${template.id}-name`}
                    label="Template name"
                    value={template.name}
                    onChange={value => {
                      updateTemplate(template.id, current => ({ ...current, name: value }));
                    }}
                    placeholder="Pro plan default"
                  />
                  <PolicyField
                    id={`registry-${template.id}-tags`}
                    label="Tags"
                    value={(template.tags ?? []).join(', ')}
                    onChange={value => {
                      updateTemplate(template.id, current => ({
                        ...current,
                        tags: value
                          .split(',')
                          .map(tag => tag.trim())
                          .filter(Boolean),
                      }));
                    }}
                    placeholder="pro, tenant, throttled"
                  />
                  <PolicyField
                    id={`registry-${template.id}-owner`}
                    label="Owner"
                    value={template.owner ?? ''}
                    onChange={value => {
                      updateTemplate(template.id, current => ({
                        ...current,
                        owner: value.trim() || undefined,
                      }));
                    }}
                    placeholder="platform-admin"
                  />
                  <label className="space-y-1.5 text-xs text-muted-foreground">
                    <div className="font-medium text-text-primary">Rollout state</div>
                    <select
                      value={template.rolloutState ?? 'active'}
                      onChange={e => {
                        updateTemplate(template.id, current => ({
                          ...current,
                          rolloutState: e.target.value as 'draft' | 'active' | 'archived',
                        }));
                      }}
                      className={selectClass}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>
                  <label className="space-y-1.5 text-xs text-muted-foreground">
                    <div className="font-medium text-text-primary">Codebase overrides</div>
                    <select
                      value={template.allowCodebaseOverride === false ? 'locked' : 'allowed'}
                      onChange={e => {
                        updateTemplate(template.id, current => ({
                          ...current,
                          allowCodebaseOverride: e.target.value !== 'locked',
                        }));
                      }}
                      className={selectClass}
                    >
                      <option value="allowed">Allowed</option>
                      <option value="locked">Template only</option>
                    </select>
                  </label>
                  <PolicyField
                    id={`registry-${template.id}-locked`}
                    label="Locked fields"
                    value={(template.lockedFields ?? []).join(', ')}
                    onChange={value => {
                      updateTemplate(template.id, current => ({
                        ...current,
                        lockedFields: value
                          .split(',')
                          .map(field => field.trim())
                          .filter(Boolean),
                      }));
                    }}
                    placeholder="plan, maxActiveGlobal"
                    description="Comma-separated policy field names locked by this template."
                  />
                </div>

                <label className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="font-medium text-text-primary">Description</div>
                  <Textarea
                    value={template.description ?? ''}
                    onChange={e => {
                      updateTemplate(template.id, current => ({
                        ...current,
                        description: e.target.value,
                      }));
                    }}
                    className="min-h-20 text-sm"
                  />
                </label>

                <label className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="font-medium text-text-primary">Policy JSON</div>
                  <Textarea
                    value={JSON.stringify(template.policy, null, 2)}
                    onChange={e => {
                      const parsed = parseWorkflowAdmissionPolicyJson(e.target.value);
                      if (parsed.error) {
                        setMessage({
                          type: 'error',
                          text: `Template "${template.name}": ${parsed.error}`,
                        });
                        return;
                      }
                      setMessage(null);
                      updateTemplate(template.id, current => ({
                        ...current,
                        policy: parsed.policy ?? {},
                      }));
                    }}
                    className="min-h-32 font-mono text-xs"
                    spellCheck={false}
                  />
                </label>

                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => {
                      setTemplates(current => current.filter(item => item.id !== template.id));
                    }}
                  >
                    Remove template
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {message && (
          <div
            className={`text-xs ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
          >
            {message.text}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={addTemplate}>
            Add template
          </Button>
          <Button
            size="sm"
            className="text-xs"
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate(templates);
            }}
          >
            {mutation.isPending ? 'Saving...' : 'Save registry'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowAuditPresetsSection({
  config,
}: {
  config: SafeConfigResponse;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() =>
    JSON.stringify(config.workflowAuditPresets ?? [], null, 2)
  );
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setDraft(JSON.stringify(config.workflowAuditPresets ?? [], null, 2));
  }, [config.workflowAuditPresets]);

  const mutation = useMutation({
    mutationFn: updateWorkflowAuditPresets,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config'] });
      setMessage({ type: 'success', text: 'Workflow audit presets saved.' });
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
    },
  });

  function handleSave(): void {
    try {
      const parsed = JSON.parse(draft) as unknown;
      if (!Array.isArray(parsed)) {
        throw new Error('Saved presets must be a JSON array.');
      }
      const presets = parsed.filter(isWorkflowAuditPreset).slice(0, 12);
      if (presets.length !== parsed.length) {
        throw new Error('Each preset must include id, name, createdAt, and state.');
      }
      mutation.mutate(presets);
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Invalid JSON' });
    }
  }

  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle>Workflow Audit Presets</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-3 text-sm text-muted-foreground">
          Saved Mission Control presets are stored as operator-scoped database preferences and used
          by the dashboard.
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">
            {String(config.workflowAuditPresets?.length ?? 0)} presets
          </Badge>
          <span>Stored in the Maestro database</span>
        </div>
        <Textarea
          value={draft}
          onChange={e => {
            setDraft(e.target.value);
          }}
          className="min-h-56 font-mono text-xs"
          spellCheck={false}
        />
        {message && (
          <div
            className={`text-xs ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}
          >
            {message.text}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="text-xs" onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save presets'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => {
              setDraft(JSON.stringify(config.workflowAuditPresets ?? [], null, 2));
              setMessage(null);
            }}
            disabled={mutation.isPending}
          >
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformConnectionsSection({
  adapter,
}: {
  adapter: string | undefined;
}): React.ReactElement {
  const platforms = [
    { name: 'Web', connected: adapter === 'web' },
    { name: 'Slack', connected: false },
    { name: 'Telegram', connected: false },
    { name: 'Discord', connected: false },
    { name: 'GitHub', connected: false },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform Connections</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {platforms.map(p => (
            <div key={p.name} className="flex items-center justify-between text-sm">
              <span>{p.name}</span>
              <Badge variant={p.connected ? 'default' : 'secondary'}>
                {p.connected ? 'Connected' : 'Not configured'}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ConcurrencySection({
  health,
}: {
  health: { concurrency: { active: number; maxConcurrent: number } } | undefined;
}): React.ReactElement {
  const active = health?.concurrency.active ?? 0;
  const max = health?.concurrency.maxConcurrent ?? 1;
  const pct = max > 0 ? Math.min((active / max) * 100, 100) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Concurrency</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${String(pct)}%` }}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {active} / {max} concurrent conversations
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationsSection(): React.ReactElement {
  const { permission, subscribed, busy, enable, disable } = usePushSubscription();

  const statusLabel: Record<typeof permission, string> = {
    unsupported: 'Unsupported',
    default: 'Not enabled',
    granted: subscribed ? 'On' : 'Allowed',
    denied: 'Blocked',
  };
  const statusVariant: 'default' | 'secondary' | 'destructive' | 'outline' =
    permission === 'denied' || permission === 'unsupported'
      ? 'destructive'
      : subscribed
        ? 'default'
        : 'secondary';

  const canEnable = permission !== 'unsupported' && permission !== 'denied' && !subscribed;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span>Push Notifications</span>
          <Badge variant={statusVariant}>{statusLabel[permission]}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Get a push notification on this device when an agent hits an approval gate, so you can
            approve or reject while away from the desk.
          </p>

          {(permission === 'unsupported' || permission === 'denied') && (
            <p className="text-sm text-muted-foreground">
              {permission === 'unsupported'
                ? 'This browser cannot receive push notifications. On iOS, install Maestro to your home screen first.'
                : 'Notifications are blocked. Enable them for this site in your browser settings, then reload.'}
            </p>
          )}

          <div className="flex items-center gap-2">
            {subscribed ? (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={(): void => {
                  void disable();
                }}
              >
                {busy ? 'Working…' : 'Disable notifications'}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                disabled={busy || !canEnable}
                onClick={(): void => {
                  void enable();
                }}
              >
                {busy ? 'Working…' : 'Enable notifications'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AIProvidersSection(): React.ReactElement {
  const queryClient = useQueryClient();
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [terminalCommand, setTerminalCommand] = useState<string | undefined>();
  const [terminalProvider, setTerminalProvider] = useState<string | undefined>();

  const handleOpenTerminal = (command: string): void => {
    // Detect which provider this auth command is for
    const provider = command.includes('codex')
      ? 'codex'
      : command.includes('claude')
        ? 'claude'
        : undefined;
    setTerminalProvider(provider);
    setTerminalCommand(command);
    setTerminalOpen(true);
  };

  const handleAuthSuccess = async (): Promise<void> => {
    if (!terminalProvider) return;

    try {
      const { importProviderCredentials } = await import('@/lib/api');
      await importProviderCredentials(terminalProvider);
    } catch {
      // Import failed — user can still manually import via button
    }

    // Refresh provider cards regardless
    void queryClient.invalidateQueries({ queryKey: ['providers'] });

    // Auto-close terminal after brief delay so user sees the success message
    setTimeout(() => {
      setTerminalOpen(false);
      setTerminalCommand(undefined);
      setTerminalProvider(undefined);
    }, 1500);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-text-primary">AI Providers</h2>
      <ProviderDashboard onOpenTerminal={handleOpenTerminal} />

      {terminalOpen && (
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <span>Terminal</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerminalOpen(false);
                  setTerminalCommand(undefined);
                  setTerminalProvider(undefined);
                }}
                className="h-6 text-xs"
              >
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <WebTerminal
              initialCommand={terminalCommand}
              onProcessSuccess={() => {
                void handleAuthSuccess();
              }}
              className="h-[400px]"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function SettingsPage(): React.ReactElement {
  const { data: authSession } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
  });

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ['config'],
    queryFn: getConfig,
  });

  const {
    data: health,
    isLoading: healthLoading,
    error: healthError,
  } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
  });

  const { data: databaseValidationSummary, error: databaseValidationSummaryError } = useQuery({
    queryKey: ['databaseValidationSummary'],
    queryFn: getDatabaseValidationSummary,
    enabled: authSession?.authenticated === true,
  });

  const { data: databaseValidationIssues, error: databaseValidationIssuesError } = useQuery({
    queryKey: ['databaseValidationIssues'],
    queryFn: getDatabaseValidationIssues,
    enabled: authSession?.authenticated === true,
  });

  const isLoading = configLoading || healthLoading;

  const SETTINGS_SECTIONS = [
    { id: 'system', label: 'System Health' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'account-auth', label: 'Account Auth' },
    { id: 'assistant', label: 'Assistant Config' },
    { id: 'policy-registry', label: 'Policy Registry' },
    { id: 'workflow-audit-presets', label: 'Workflow Audit Presets' },
    { id: 'platforms', label: 'Platforms' },
    { id: 'providers', label: 'AI Providers' },
    // These three panels have rendered on this page since they were written but were
    // absent from this list, so they had no nav entry and were reachable only by
    // scrolling past nine other sections. `provisioning` in particular is the Vault
    // break-glass ceremony's UI — a complete collect-here/apply-there flow that was
    // effectively undiscoverable. Ordered to match the DOM order below, so the scroll
    // target matches the click.
    { id: 'operator-org', label: 'Operator Org' },
    { id: 'managed-config', label: 'Managed Config' },
    { id: 'provisioning', label: 'Credential Provisioning' },
    { id: 'mos-audit-federation', label: 'MOS Audit Federation' },
    { id: 'deployment-pipeline', label: 'Deployment Pipeline' },
    { id: 'deploy-mesh-setup', label: 'Deploy Mesh Setup' },
    { id: 'project-sources', label: 'Project Sources' },
    { id: 'projects', label: 'Projects' },
    { id: 'validation', label: 'Database Validation' },
    { id: 'storage', label: 'Secure Storage' },
  ] as const;

  const [activeSection, setActiveSection] = useState('system');

  const handleNavClick = (id: string): void => {
    setActiveSection(id);
    const el = document.getElementById(`settings-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Settings" />
      <div className="flex flex-1 overflow-hidden">
        {/* Settings sidebar navigation */}
        <nav
          data-testid="settings-sidebar"
          className="hidden md:flex w-56 shrink-0 border-r border-border/70 bg-surface/80 p-4 backdrop-blur-sm overflow-auto"
        >
          <div className="space-y-4">
            <div className="rounded-2xl border border-border/60 bg-background/70 px-4 py-3 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                Settings Atlas
              </div>
              <div className="mt-2 text-sm text-text-secondary">
                Move between system health, governance, providers, projects, and storage controls.
              </div>
            </div>
            <div className="rounded-2xl border border-border/70 bg-surface/90 p-2 shadow-sm">
              {SETTINGS_SECTIONS.map(section => (
                <button
                  key={section.id}
                  data-testid={`settings-nav-${section.id}`}
                  type="button"
                  onClick={(): void => {
                    handleNavClick(section.id);
                  }}
                  className={`w-full rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-elevated'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Settings content */}
        <PageBody maxWidthClassName="max-w-5xl" contentClassName="space-y-6">
          <section className="rounded-[28px] border border-border/70 bg-[linear-gradient(135deg,rgba(59,130,246,0.12),rgba(15,23,42,0.02)_42%,rgba(234,179,8,0.1))] p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                  Platform Control
                </p>
                <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
                  Configure the operating surface
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-text-secondary">
                  Tune health, governance, providers, project policies, validation, and secure
                  storage from one control plane.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                    Sections
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-text-primary">
                    {SETTINGS_SECTIONS.length}
                  </div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                    Active Focus
                  </div>
                  <div className="mt-2 text-sm font-medium text-text-primary">
                    {SETTINGS_SECTIONS.find(section => section.id === activeSection)?.label ??
                      'System'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {(configError || healthError) && (
            <div className="text-sm text-destructive">
              Failed to load settings:{' '}
              {((): string => {
                const err = configError ?? healthError;
                return err instanceof Error ? err.message : 'Unknown error';
              })()}
              . Check that the server is running.
            </div>
          )}

          {isLoading && <div className="text-sm text-muted-foreground">Loading settings...</div>}

          <div id="settings-system" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <SystemHealthSection health={health} database={configData?.database} />
            <ConcurrencySection health={health} />
          </div>

          <div id="settings-notifications" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <NotificationsSection />
          </div>

          <div id="settings-account-auth">
            <AccountAuthSection />
          </div>

          <div id="settings-assistant" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {configData && <AssistantConfigSection config={configData.config} />}
            <div id="settings-platforms">
              <PlatformConnectionsSection adapter={health?.adapter} />
            </div>
          </div>

          <div id="settings-policy-registry">
            {configData && <PolicyRegistrySection config={configData.config} />}
          </div>

          <div id="settings-workflow-audit-presets">
            {configData && <WorkflowAuditPresetsSection config={configData.config} />}
          </div>

          <div id="settings-providers">
            <AIProvidersSection />
          </div>

          <div id="settings-operator-org">
            <OperatorOrgPanel />
          </div>

          <div id="settings-managed-config">
            <ManagedConfigPanel />
          </div>

          <div id="settings-provisioning">
            <ProvisionPanel />
          </div>

          <div id="settings-mos-audit-federation">
            <MosAuditFederationPanel />
          </div>

          <div id="settings-deployment-pipeline">
            <DeploymentPipelinePanel />
          </div>

          <div id="settings-deploy-mesh-setup">
            <DeployMeshSetupPanel />
          </div>

          <div id="settings-project-sources">
            <ProjectSourceCatalogSection operatorRole={authSession?.operator?.role} />
          </div>

          <div id="settings-projects">
            <ProjectsSection
              registryTemplates={configData?.config.workflowAdmissionRegistry?.templates ?? []}
            />
          </div>

          <div id="settings-validation">
            <DatabaseValidationSection
              sessionAuthenticated={authSession?.authenticated === true}
              summary={databaseValidationSummary}
              issues={databaseValidationIssues}
              summaryError={
                databaseValidationSummaryError instanceof Error
                  ? databaseValidationSummaryError
                  : null
              }
              issuesError={
                databaseValidationIssuesError instanceof Error
                  ? databaseValidationIssuesError
                  : null
              }
            />
          </div>

          <div id="settings-storage">
            <SecureStorageSection />
          </div>
        </PageBody>
      </div>
    </div>
  );
}
